import { FilterBar } from '@app/components/FilterBar';
import { FiltersContainer } from '@app/components/Filters/FiltersContainer';
import { VolumeDiscountBanner } from '@app/components/VolumeDiscountBanner';
import { EXPERIENCE } from '@app/data/clients/rbServerSideClient/utils/constants';
import {
  Box,
  constants,
  PageSection,
  Skeleton,
} from '@redbubble/design-system';
import { ErrorBoundary } from '@redbubble/next/error-boundary';
import { useViewportWidth } from '@redbubble/next/hooks';
import { AppProps, DeviceType, UserInfo } from '@redbubble/next/types';
import {
  useNavigation,
  usePageProps,
  useUserInfo,
} from '@redbubble/next-provider';
import {
  RSPageType,
  useRSPageView,
} from '@redbubble/next-provider/rudderstack';
import dynamic from 'next/dynamic';
import { NextPage } from 'next/types';
import { useEffect, useState } from 'react';
import { PreconnectLinks } from '../../components/ResourceHints';
import { SignUpGuardProvider } from '../../hooks/SignUpGuardContext';
import searchUUIDStorage, {
  SearchUUIDStorageKey,
} from '../../utils/searchUUIDStorage';
import { LoadingBoundary } from '../i/components/LoadingBoundary';
import { TrustSignalsGroup } from '../i/components/TrustSignalsGroup';
import { Breadcrumbs } from './components/Breadcrumbs';
import { NoSearchResults } from './components/NoSearchResults/NoSearchResults';
import { Pagination } from './components/Pagination/Pagination';
import { RelatedTopicsSection } from './components/RelatedTopicsSection';
import { SEOTags, SEOText } from './components/SEO';
import { PageMetadata } from './components/SEO/PageMetadata';
import { SearchHeader } from './components/SearchHeader';
import { SearchResultsGrid } from './components/SearchResultsGrid';
import { DynamicSubscribeBannerFooter } from './components/SubscribeBanner/DynamicSubscribeBanner';
import { SuggestionBanner } from './components/SuggestionBanner';
import styles from './index.module.scss';
import type { FilterDrawerSettings, SearchResultsPageProps } from './types';
import { trackGA4ViewSearchResults } from './utils/analytics';

const ReactToastContainer = dynamic(
  () =>
    import(
      /* webpackChunkName: "ReactToastContainer" */
      '../../components/Toasts/ReactToastContainer'
    ).then(({ ReactToastContainer: Component }) => Component),
  { ssr: false, loading: () => null }
);

const SignUpGuard = dynamic(
  () =>
    import(
      /* webpackChunkName: "SignUpGuard" */
      '../../components/SignUpGuard/SignUpGuard'
    ).then(({ SignUpGuard: Component }) => Component),
  { ssr: false, loading: () => null }
);

const SearchResultsPage: NextPage<SearchResultsPageProps> = ({
  breadcrumbs,
  bubblesData,
  category,
  categoryOptions,
  filters,
  formattedQuery,
  heroData,
  lastClickedItemId,
  pagination,
  results,
  searchPageType,
  seoFooterText,
  seoMetadata,
  sortOptions,
  suggestion,
  title,
  volumeDiscountThresholds,
  recommendedProducts,
  searchUUID,
  topic,
  searchContext,
  searchTerm,
  resultCount,
  relatedTopics,
}) => {
  useRSPageView(RSPageType.Search);
  const { navigate, loading } = useNavigation();
  const [{ isLoggedIn, locale }] = useUserInfo<UserInfo>();
  const categoryName = searchContext?.category;
  const { preconnectLinks, deviceType } = usePageProps<
    AppProps & SearchResultsPageProps
  >();

  useEffect(() => {
    if (searchUUID)
      searchUUIDStorage.set(SearchUUIDStorageKey.Latest, searchUUID);
  }, [searchUUID]);

  const [filterDrawerSettings, setFilterDrawerSettings] =
    useState<FilterDrawerSettings>({
      open: false,
    });

  const handlePagination = (url: string) => {
    navigate({ url });
  };

  const { isOrAbove } = useViewportWidth(deviceType);
  const isMobile = !isOrAbove(DeviceType.Tablet);

  const [{ federatedId }] = useUserInfo<UserInfo>();

  // biome-ignore lint/correctness/useExhaustiveDependencies: useExhaustiveDependencies
  useEffect(() => {
    trackGA4ViewSearchResults(
      bubblesData,
      category,
      formattedQuery,
      searchPageType,
      pagination,
      relatedTopics,
      resultCount,
      searchContext,
      searchUUID,
      topic
    );
    // we only want this to fire if the results or the federated id changes
    // an update in the federated id means the user has logged in or out
    // this isnt important yet as there is no way to ajax signin anymore but provides a guard for future changes
    // an update in results means there is new search data
    // we know the other dependencies exist as provided from get serverside props
    // but the update should occur only when the two mentioned above change
    // likewise these additional props should only really change when new results are provided
  }, [results, federatedId]);

  const seoVisible = locale === 'en';

  return (
    <>
      <PreconnectLinks preconnectLinks={preconnectLinks} />
      <SignUpGuardProvider>
        <SignUpGuard />
        <Box element="main">
          {seoMetadata && <PageMetadata seoMetadata={seoMetadata} />}

          <PageSection size={constants.MEDIUM}>
            <Box className={styles.primaryContent}>
              {searchTerm && results.length === 0 ? (
                <>
                  {suggestion && (
                    <Box marginTop="l" marginBottom="none">
                      <SuggestionBanner
                        suggestion={suggestion}
                        formattedQuery={formattedQuery}
                      />
                    </Box>
                  )}
                  <NoSearchResults searchTerm={searchTerm} />
                </>
              ) : (
                <>
                  <ErrorBoundary>
                    <SearchHeader heroData={heroData} title={title} />
                  </ErrorBoundary>

                  {suggestion && (
                    <SuggestionBanner
                      suggestion={suggestion}
                      formattedQuery={formattedQuery}
                    />
                  )}
                  {bubblesData && (
                    <ErrorBoundary>
                      <RelatedTopicsSection
                        relatedTopicsData={bubblesData}
                        seoVisible={seoVisible}
                      />
                    </ErrorBoundary>
                  )}

                  {sortOptions && categoryOptions && (
                    <LoadingBoundary
                      loadingComponent={<Skeleton animated height={40} />}
                    >
                      <FilterBar
                        categoryOptions={categoryOptions}
                        sortOptions={sortOptions}
                        onClick={(openProductSheet) =>
                          setFilterDrawerSettings({
                            open: true,
                            openProductSheet,
                          })
                        }
                        appliedCount={filters?.appliedCount}
                        totalResults={resultCount}
                      />
                    </LoadingBoundary>
                  )}

                  {volumeDiscountThresholds &&
                    volumeDiscountThresholds.length && (
                      <ErrorBoundary>
                        <LoadingBoundary
                          loadingComponent={
                            <Skeleton
                              height={50}
                              animated
                              data-testid="volume-discount-skeleton"
                            />
                          }
                        >
                          <VolumeDiscountBanner
                            volumeDiscountThresholds={volumeDiscountThresholds}
                          />
                        </LoadingBoundary>
                      </ErrorBoundary>
                    )}

                  <SearchResultsGrid
                    searchResults={results}
                    recommendedProducts={recommendedProducts}
                    lastClickedItemId={lastClickedItemId}
                    loading={loading}
                    searchUUID={searchUUID}
                    topic={topic}
                    categoryName={categoryName}
                  />

                  {pagination && (
                    <nav>
                      <Pagination
                        firstGap={pagination.firstGap}
                        middlePages={pagination.middlePages}
                        lastGap={pagination.lastGap}
                        firstPage={pagination.firstPage}
                        lastPage={pagination.lastPage}
                        nextPage={pagination.nextPage}
                        previousPage={pagination.previousPage}
                        handlePagination={handlePagination}
                      />
                    </nav>
                  )}

                  <ErrorBoundary>
                    {Array.isArray(breadcrumbs) && breadcrumbs.length > 0 && (
                      <Breadcrumbs breadcrumbs={breadcrumbs} />
                    )}

                    {seoFooterText && <SEOText seoFooterText={seoFooterText} />}

                    {seoMetadata && (
                      <SEOTags relatedTagLinks={seoMetadata.relatedTagLinks} />
                    )}

                    <TrustSignalsGroup />
                  </ErrorBoundary>
                </>
              )}
            </Box>
          </PageSection>

          {!isLoggedIn && (
            <ErrorBoundary>
              <DynamicSubscribeBannerFooter />
            </ErrorBoundary>
          )}

          {filters && filterDrawerSettings.open && (
            <FiltersContainer
              filterDrawerSettings={filterDrawerSettings}
              onClose={() => setFilterDrawerSettings({ open: false })}
              filters={filters}
              from={isMobile ? constants.BOTTOM : constants.LEFT}
              experience={EXPERIENCE.SRP}
            />
          )}
          <ReactToastContainer />
        </Box>
      </SignUpGuardProvider>
    </>
  );
};

export default SearchResultsPage;
